import { useState, useEffect, useCallback, useRef } from "react";
import type { OrderLine, Configuration, ProductOption } from "../types";
import type { WorkDetails } from "../../WorkDetailsForm";
import { DEFAULT_VAT_RATE } from "../types";

interface UseOrderLinesResult {
  orderLines: OrderLine[];
  setOrderLines: React.Dispatch<React.SetStateAction<OrderLine[]>>;
  configurationLineId: string | null;
  setConfigurationLineId: React.Dispatch<React.SetStateAction<string | null>>;
  editingPrices: Record<string, string>;
  setEditingPrices: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  addOrderLine: () => void;
  updateOrderLine: (id: string, field: keyof OrderLine, value: string | number | boolean | null | Record<string, string> | WorkDetails) => void;
  removeOrderLine: (id: string) => void;
  selectProduct: (lineId: string, productId: string, products: ProductOption[] | undefined) => void;
  getDisplayPrice: (line: OrderLine) => number;
  getAlternativePrice: (line: OrderLine) => { label: string; value: number };
}

export function useOrderLines(configuration: Configuration | null): UseOrderLinesResult {
  const [orderLines, setOrderLines] = useState<OrderLine[]>([]);
  const [configurationLineId, setConfigurationLineId] = useState<string | null>(null);
  const [editingPrices, setEditingPrices] = useState<Record<string, string>>({});

  // Ref to track configurationLineId inside effect without causing re-runs
  const configLineIdRef = useRef(configurationLineId);
  configLineIdRef.current = configurationLineId;

  // Auto-create/update order line when configuration changes
  useEffect(() => {
    const currentLineId = configLineIdRef.current;

    if (configuration) {
      const lineDescription = `${configuration.productModelName} (konfiguroitu)`;
      const basePrice = 0;
      const vatRate = DEFAULT_VAT_RATE;

      if (currentLineId) {
        setOrderLines(prev => prev.map(line =>
          line.id === currentLineId
            ? { ...line, description: lineDescription, unit_price: basePrice, line_total: basePrice }
            : line
        ));
      } else {
        const newLineId = crypto.randomUUID();
        const newLine: OrderLine = {
          id: newLineId,
          product_id: null,
          configuration_id: null,
          description: lineDescription,
          quantity: 1,
          unit_price: basePrice,
          vat_rate: vatRate,
          line_total: basePrice,
          price_includes_vat: false,
        };
        setOrderLines(prev => [...prev, newLine]);
        setConfigurationLineId(newLineId);
      }
    } else if (currentLineId) {
      setOrderLines(prev => prev.filter(line => line.id !== currentLineId));
      setConfigurationLineId(null);
    }
  }, [configuration?.productModelId, configuration?.productModelName]);

  const addOrderLine = useCallback(() => {
    const newLine: OrderLine = {
      id: crypto.randomUUID(),
      product_id: null,
      description: "",
      quantity: 1,
      unit_price: 0,
      vat_rate: DEFAULT_VAT_RATE,
      line_total: 0,
      price_includes_vat: false,
      work_details: null,
    };
    setOrderLines(prev => [...prev, newLine]);
  }, []);

  const updateOrderLine = useCallback((id: string, field: keyof OrderLine, value: string | number | boolean | null | Record<string, string> | WorkDetails) => {
    setOrderLines(prev => prev.map((line) => {
      if (line.id === id) {
        const updated = { ...line, [field]: value };
        
        if (field === "quantity" || field === "unit_price" || field === "vat_rate" || field === "price_includes_vat") {
          if (field === "unit_price" && updated.price_includes_vat) {
            const grossPrice = value as number;
            updated.unit_price = Math.round(grossPrice / (1 + updated.vat_rate / 100) * 100) / 100;
          } else if (field === "price_includes_vat" && value === true) {
            const grossPrice = updated.unit_price;
            updated.unit_price = Math.round(grossPrice / (1 + updated.vat_rate / 100) * 100) / 100;
          } else if (field === "vat_rate" && updated.price_includes_vat) {
            const grossPrice = updated.unit_price * (1 + (line.vat_rate / 100));
            updated.unit_price = Math.round(grossPrice / (1 + (value as number) / 100) * 100) / 100;
          }
          updated.line_total = Math.round(updated.quantity * updated.unit_price * 100) / 100;
        }
        return updated;
      }
      return line;
    }));
  }, []);

  const removeOrderLine = useCallback((id: string) => {
    setOrderLines(prev => prev.filter(line => line.id !== id));
  }, []);

  const selectProduct = useCallback((lineId: string, productId: string, products: ProductOption[] | undefined) => {
    if (productId === "none") {
      setOrderLines(prev => prev.map(line =>
        line.id === lineId ? { ...line, product_id: null } : line
      ));
      return;
    }

    const product = products?.find(p => p.id === productId);
    if (!product) return;

    const netPrice = product.price_includes_vat
      ? product.price / (1 + product.vat_rate / 100)
      : product.price;

    setOrderLines(prev => prev.map(line => {
      if (line.id === lineId) {
        return {
          ...line,
          product_id: productId,
          description: product.description || product.name,
          unit_price: netPrice,
          vat_rate: product.vat_rate,
          line_total: line.quantity * netPrice,
          price_includes_vat: product.price_includes_vat ?? false,
        };
      }
      return line;
    }));
  }, []);

  const getDisplayPrice = useCallback((line: OrderLine): number => {
    if (line.price_includes_vat) {
      return line.unit_price * (1 + line.vat_rate / 100);
    }
    return line.unit_price;
  }, []);

  const getAlternativePrice = useCallback((line: OrderLine): { label: string; value: number } => {
    if (line.price_includes_vat) {
      return { label: "Veroton", value: line.unit_price };
    }
    return { label: "Verollinen", value: line.unit_price * (1 + line.vat_rate / 100) };
  }, []);

  return {
    orderLines,
    setOrderLines,
    configurationLineId,
    setConfigurationLineId,
    editingPrices,
    setEditingPrices,
    addOrderLine,
    updateOrderLine,
    removeOrderLine,
    selectProduct,
    getDisplayPrice,
    getAlternativePrice,
  };
}
